import { useMemo } from 'react'
import {
  ProductFeatureV2,
  RoundRobinExpectedHostV2,
} from '@chilipiper/api-client/src/user-availability'
import { PublicRoundRobinSchedulingLink } from '@chilipiper/api-client/src/team-scheduling'
import {
  BookerSettings,
  getRoundRobinAndOwnershipExpectedHost,
} from '../../load-availability-options'
import { WithDistribution } from '../types'
import {
  getDistributionIdsFromRoundRobinAssignments,
  transformRoundRobinAssignmentsToRoutingResult,
  applySelectedAssigneesToWithDistribution,
} from '../helpers'
import { getLinkProductFeature } from '../helpers/product-feature'
import { useDistributionsUserList } from '../api-queries'
import { useParsedGuest } from './use-parsed-guest'

type RoundRobinMatch = {
  expectedHost: RoundRobinExpectedHostV2
  roundRobinLinkRoutingResult: WithDistribution
  primaryGuestEmail?: string
}

export const useRoundRobinRouting = ({
  tenantId,
  roundRobinSchedulingLink,
  smartParameterId,
  productFeature,
  bookerSettings,
  selectedAssigneeIds,
}: {
  tenantId: string
  roundRobinSchedulingLink?: Pick<PublicRoundRobinSchedulingLink, 'distributionIds'>
  smartParameterId?: string | null
  productFeature?: ProductFeatureV2
  bookerSettings?: BookerSettings
  selectedAssigneeIds?: string[]
}): RoundRobinMatch | undefined => {
  const linkProductFeature = getLinkProductFeature(productFeature)

  const parsedGuest = useParsedGuest({
    smartParameterId,
    linkProductFeature,
  })

  const primaryGuestEmail = parsedGuest?.guestEmail
  const assignments =
    linkProductFeature?.type === 'RoundRobinSchedulingLink'
      ? linkProductFeature.assignments
      : undefined

  const distributionIds = useMemo(() => {
    if (assignments) {
      return getDistributionIdsFromRoundRobinAssignments(
        assignments,
        roundRobinSchedulingLink?.distributionIds
      )
    }

    return roundRobinSchedulingLink?.distributionIds
  }, [assignments, roundRobinSchedulingLink?.distributionIds])

  const distributions = useDistributionsUserList({
    tenantId,
    distributionIds,
    productFeature: 'RoundRobinSchedulingLink',
  })

  const roundRobinLinkRoutingResult = assignments
    ? transformRoundRobinAssignmentsToRoutingResult(assignments, distributions)
    : {
        distributions: distributions,
      }

  // Apply selectedAssigneeId to the routing result if provided
  const updatedRoutingResult = applySelectedAssigneesToWithDistribution(
    roundRobinLinkRoutingResult,
    selectedAssigneeIds
  )

  if (!updatedRoutingResult || updatedRoutingResult.distributions.length === 0) {
    return undefined
  }

  const expectedHost = getRoundRobinAndOwnershipExpectedHost({
    bookerSettings,
    firstAssignment: { distributionId: updatedRoutingResult.distributions[0].id },
  })

  return {
    primaryGuestEmail,
    roundRobinLinkRoutingResult: updatedRoutingResult,
    expectedHost,
  }
}
